import React, { useState, useEffect, useRef } from 'react';
import CardContent from '@mui/material/CardContent';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { GridWrap, CarouselWrap } from 'styles/Wrap.style';
import CardsCarouselProps from 'interfaces/CardsCarouselProps.interface';
import CarouselCardImage from './CarouselCardImage';
import { CarouselLButton, CarouselRButton } from './CarouselButton';
import CarouselStyle from 'styles/Carousel.style';
import CardRow from './CardRow';
import ProgressDots from './ProgressDots';
import { hasAuthTokens } from 'index';

const CARD_WIDTH = 240;
const CARD_GAP = 24;
const CARD_SPACE = CARD_WIDTH + CARD_GAP;

const CardsCarousel: React.FC<CardsCarouselProps> = ({
  apps,
  user,
  card,
  cardInner,
  cardTitleWrap,
  cardTitle,
  navigate,
  containerStyle
}) => {
  const [cardsToShow, setCardsToShow] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [, setContainerWidth] = useState(0);
  const [currentProgressDot, setCurrentProgressDot] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [pointerStartX, setPointerStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const effectiveWidth = containerRef.current?.clientWidth ?? document.body.clientWidth;
      setContainerWidth(effectiveWidth);
      const count = Math.floor((effectiveWidth + CARD_GAP) / (CARD_WIDTH + CARD_GAP));
      setCardsToShow(count > 0 ? count : 1);
    };

    const handleCarousel = () => {
      setTranslateX(0);
      setCurrentProgressDot(0);
    };

    window.addEventListener('load', handleResize);
    window.addEventListener('resize', handleResize);
    window.addEventListener('load', handleCarousel);
    window.addEventListener('resize', handleCarousel);

    handleResize();
    handleCarousel();

    return () => {
      window.removeEventListener('load', handleResize);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', handleCarousel);
      window.removeEventListener('resize', handleCarousel);
    };
  }, []);

  const dotCount: number = Math.ceil(apps.length / cardsToShow);

  const handleCarouselMovement = class {
    public static handlePrev = () => {
      setTranslateX(Math.max(0, translateX - CARD_SPACE));
      setCurrentProgressDot(Math.max(0, currentProgressDot - 1));
    };

    public static handleNext = () => {
      // Calculate max scroll so last item is always visible
      const maxScroll = Math.max(0, (apps.length - cardsToShow) * CARD_SPACE);
      const nextTranslateX = Math.min(translateX + CARD_SPACE, maxScroll);
      setTranslateX(nextTranslateX);
      setCurrentProgressDot(Math.min(dotCount - 1, currentProgressDot + 1));
    };
  };

  const handleCarouselSwipe = class {
    public static handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      (e.target as HTMLElement).setPointerCapture(e.pointerId)
      setPointerStartX(e.clientX);
      setIsDragging(false);
    }

    public static handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
      const delta = e.clientX - pointerStartX;
      if (Math.abs(delta) > CARD_WIDTH / 3) {
        setIsDragging(true);
        if (delta < 0 && translateX < apps.length * CARD_SPACE) {
          handleCarouselMovement.handleNext();
        } else if (delta > 0 && translateX > 0) {
          handleCarouselMovement.handlePrev();
        }
      } else {
        setIsDragging(false);
      }
      setPointerStartX(0);
    };

    public static handlePointerCancel = () => {
      setTranslateX(0);
      setIsDragging(false);
    };
  };

  const isGridWrap = containerStyle && containerStyle.display === 'inline-flex' && containerStyle.flexWrap === 'wrap';

  if (isGridWrap) {
    return (
      <div style={CarouselWrap(containerStyle)}>
        {apps.map((app, index) => (
          <div style={CarouselStyle(card, CARD_WIDTH)} key={index}>
            <div style={cardInner}>
              <CardContent style={{ padding: '0', margin: '0' }}>
                <a
                  href={app.link}
                  target={user ? '_blank' : undefined}
                  onClick={(e) => {
                    if (isDragging) {
                      e.preventDefault();
                      return;
                    }
                    if (!user || !hasAuthTokens()) {
                      e.preventDefault();
                      navigate('/login');
                    }
                  }}
                >
                  <CarouselCardImage app={app} />
                </a>
              </CardContent>
              <div style={cardTitleWrap}>
                <div style={cardTitle}>{app.name}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <div
        ref={containerRef}
        onPointerDown={handleCarouselSwipe.handlePointerDown}
        onPointerUp={handleCarouselSwipe.handlePointerUp}
        onPointerCancel={handleCarouselSwipe.handlePointerCancel}
        style={{
          ...GridWrap(containerStyle),
          touchAction: 'none'
        }}
      >
        {}
        {translateX > 0 && (
          <button
            style={CarouselLButton}
            onClick={handleCarouselMovement.handlePrev}
            aria-label="Scroll left"
          >
            <ArrowBackIosIcon fontSize="small" />
          </button>
        )}
        <div
          ref={rowRef}
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: CARD_GAP,
            transition: 'transform 0.75s ease-in-out',
            width: apps.length * (CARD_WIDTH + CARD_GAP),
            transform: `translateX(${translateX * -1}px)`,
          }}
        >
          <CardRow
            apps={apps}
            user={user}
            width={CARD_WIDTH}
          />
        </div>
        {}
        {translateX < Math.max(0, (apps.length - cardsToShow) * CARD_SPACE) && (
          <button
            style={CarouselRButton}
            onClick={handleCarouselMovement.handleNext}
            aria-label="Scroll right"
          >
            <ArrowForwardIosIcon fontSize="small" />
          </button>
        )}
      </div>
      <ProgressDots
        dotCount={dotCount}
        currentIndex={currentProgressDot}
      />
    </div>
  );
};

export default CardsCarousel;
